import type { HelmCommunication, WorkflowEvent } from './api/types'
import { greetingLine } from './customer-messages'

/**
 * How a record's timeline reads: workflow events in words, the owner and
 * due-date changes in particular, and the message that tells a customer a due
 * date moved. Shared by the inquiry and consignment pages, and kept free of
 * Helm's path aliases so the API's tests can import it.
 */

function formatLabel(value: string | null | undefined) {
  if (!value) return 'Not set'
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/** "28 Oct": a due date as Kampala reads it. */
export function kampalaDay(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Africa/Kampala',
  })
}

/** Names a person on the timeline: null is nobody, an unknown id is someone. */
export type NameOf = (userId: string | null | undefined) => string | null

function text(value: unknown) {
  return typeof value === 'string' && value ? value : null
}

/** "Jonas moved the due date from 24 Oct to 28 Oct", or null for other events. */
export function ownerOrDueChange(event: WorkflowEvent, nameOf: NameOf) {
  const from = text(event.metadata?.from)
  const to = text(event.metadata?.to)
  const actor = nameOf(event.actor_user_id) ?? 'Someone'
  if (event.event_type === 'OWNER_CHANGED') {
    return {
      title: `Owner changed from ${nameOf(from) ?? 'Unassigned'} to ${nameOf(to) ?? 'Unassigned'}`,
      description: `By ${actor}`,
    }
  }
  if (event.event_type === 'DUE_DATE_CHANGED') {
    return {
      title:
        from && to
          ? `${actor} moved the due date from ${kampalaDay(from)} to ${kampalaDay(to)}`
          : to
            ? `${actor} set the due date to ${kampalaDay(to)}`
            : from
              ? `${actor} cleared the due date (it was ${kampalaDay(from)})`
              : `${actor} changed the due date`,
      description: '',
    }
  }
  return null
}

/** "Approved from Beacon": a change someone approved there rather than made on the page. */
export const VIA_BEACON = 'Approved from Beacon'

function viaBeacon(event: WorkflowEvent) {
  return event.metadata?.via === 'beacon'
}

/** A line Beacon's WhatsApp digest wrote: informational, with no record change and no message text. */
export const WHATSAPP_DIGEST = 'WhatsApp digest from Beacon'

export function whatsappDigest(event: WorkflowEvent) {
  if (event.event_type !== 'COMMENT_ADDED' || event.metadata?.source !== 'whatsapp_digest')
    return null
  return {
    title: WHATSAPP_DIGEST,
    description: [event.comment, 'Informational only; no record was changed.']
      .filter(Boolean)
      .join('\n'),
  }
}

/** "Jonas cancelled the inquiry: the client bought locally", or null for other events. */
export function cancellationChange(event: WorkflowEvent, nameOf: NameOf) {
  if (event.event_type !== 'STATE_CHANGED') return null
  const actor = nameOf(event.actor_user_id) ?? 'Someone'
  if (event.metadata?.action === 'CANCELLED') {
    return {
      title: event.comment
        ? `${actor} cancelled the inquiry: ${event.comment}`
        : `${actor} cancelled the inquiry`,
      description: '',
    }
  }
  if (event.metadata?.action === 'REOPENED') {
    return { title: `${actor} reopened the inquiry`, description: event.comment ?? '' }
  }
  return null
}

const TASK_STATUS_WORDS: Record<string, string> = {
  TODO: 'Todo',
  IN_PROGRESS: 'In Progress',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
}

/**
 * 'Jonas completed "Book sea freight"': a task added or changed, by name, or
 * null for other events and for task events that do not name their task.
 * Task titles are the team's own words, so only staff timelines say them.
 */
export function taskChange(event: WorkflowEvent, nameOf: NameOf) {
  if (!event.event_type.startsWith('TASK_')) return null
  const metadata = event.metadata ?? {}
  const title = text(metadata.title)
  if (!title) return null
  const actor = nameOf(event.actor_user_id) ?? 'Someone'
  const task = `"${title}"`
  const comment = event.comment ?? ''
  const has = (field: string) => `to_${field}` in metadata
  const status = (value: unknown) => TASK_STATUS_WORDS[String(value)] ?? formatLabel(String(value))
  const person = (value: unknown) => nameOf(text(value)) ?? 'nobody'

  if (event.event_type === 'TASK_CREATED') {
    const assignee = text(metadata.assignee_user_id)
    return {
      title: `${actor} added the task ${task}`,
      description: [assignee ? `For ${nameOf(assignee) ?? 'Someone'}` : null, comment]
        .filter(Boolean)
        .join('\n'),
    }
  }

  const changes = [
    has('status')
      ? `Status: ${status(metadata.from_status)} → ${status(metadata.to_status)}`
      : null,
    has('assignee_user_id')
      ? `Assignee: ${person(metadata.from_assignee_user_id)} → ${person(metadata.to_assignee_user_id)}`
      : null,
    has('title') ? `Renamed from "${text(metadata.from_title) ?? ''}"` : null,
    has('due_at')
      ? text(metadata.to_due_at)
        ? `Due ${kampalaDay(text(metadata.to_due_at)!)}`
        : 'Due date cleared'
      : null,
    has('priority')
      ? `Priority: ${formatLabel(text(metadata.from_priority))} → ${formatLabel(text(metadata.to_priority))}`
      : null,
  ].filter((line): line is string => Boolean(line))

  // One change reads as a sentence; several, as a list under the task.
  if (changes.length === 1 && has('status')) {
    const to = String(metadata.to_status)
    return {
      title:
        to === 'DONE'
          ? `${actor} completed ${task}`
          : to === 'CANCELLED'
            ? `${actor} cancelled ${task}`
            : to === 'IN_PROGRESS'
              ? `${actor} started ${task}`
              : `${actor} moved ${task} back to Todo`,
      description: comment,
    }
  }
  if (changes.length === 1 && has('assignee_user_id')) {
    const to = text(metadata.to_assignee_user_id)
    return {
      title: to
        ? `${actor} assigned ${task} to ${nameOf(to) ?? 'Someone'}`
        : `${actor} unassigned ${task}`,
      description: comment,
    }
  }
  return {
    title:
      event.event_type === 'TASK_COMPLETED'
        ? `${actor} completed ${task}`
        : `${actor} updated the task ${task}`,
    description: [...changes, comment].filter(Boolean).join('\n'),
  }
}

export interface WorkflowEventOptions {
  /** Say tasks by name. For staff: task titles are the team's own words. */
  taskTitles?: boolean
}

/** One workflow event as a timeline entry's title and description. */
export function workflowEventActivity(
  event: WorkflowEvent,
  nameOf: NameOf,
  options: WorkflowEventOptions = {},
) {
  const entry = describeWorkflowEvent(event, nameOf, options)
  if (!viaBeacon(event)) return entry
  return {
    ...entry,
    description: [entry.description, VIA_BEACON].filter(Boolean).join('\n'),
  }
}

function describeWorkflowEvent(
  event: WorkflowEvent,
  nameOf: NameOf,
  options: WorkflowEventOptions,
) {
  const change =
    ownerOrDueChange(event, nameOf) ??
    cancellationChange(event, nameOf) ??
    whatsappDigest(event) ??
    (options.taskTitles ? taskChange(event, nameOf) : null)
  if (change) return change
  return {
    title:
      event.event_type === 'PHASE_CHANGED'
        ? `Workflow moved to ${formatLabel(event.to_phase)}`
        : event.event_type === 'STATE_CHANGED'
          ? `Work marked ${formatLabel(event.to_state)}`
          : event.event_type.startsWith('TASK_')
            ? formatLabel(event.event_type)
            : event.event_type === 'COMMENT_ADDED'
              ? viaBeacon(event)
                ? `${nameOf(event.actor_user_id) ?? 'Someone'} via Beacon`
                : 'Workflow comment'
              : formatLabel(event.event_type),
    description: [
      event.comment,
      event.from_phase && event.to_phase
        ? `${formatLabel(event.from_phase)} → ${formatLabel(event.to_phase)}`
        : null,
      event.from_state && event.to_state
        ? `${formatLabel(event.from_state)} → ${formatLabel(event.to_state)}`
        : null,
    ]
      .filter(Boolean)
      .join('\n'),
  }
}

/** Marks a message sent to tell a customer their due date moved. */
export const DUE_DATE_NOTICE = 'DUE_DATE_NOTICE'

export function isDueDateNotice(communication: Pick<HelmCommunication, 'metadata'>) {
  return communication.metadata?.purpose === DUE_DATE_NOTICE
}

/** One communication as a timeline entry. */
export function communicationActivity(communication: HelmCommunication) {
  return {
    title: isDueDateNotice(communication)
      ? `Told the customer the new due date (${formatLabel(communication.channel)})`
      : `${formatLabel(communication.channel)} ${formatLabel(communication.direction).toLowerCase()}`,
    description: [communication.subject, communication.recipient].filter(Boolean).join('\n'),
    timestamp: communication.sent_at || communication.created_at,
    label: formatLabel(communication.status),
  }
}

export interface DueDateNoticeInput {
  kind: 'inquiry' | 'consignment'
  customerName: string | null
  /**
   * How the customer knows the record: for a shipment its Xtellar reference and
   * title (shipmentReference), for an inquiry what they asked for.
   */
  reference: string
  dueAt: string
  reason: string
}

export function dueDateNoticeSubject({
  kind,
  reference,
}: Pick<DueDateNoticeInput, 'kind' | 'reference'>) {
  return kind === 'consignment'
    ? `New date for your shipment${reference ? ` ${reference}` : ''}`
    : `New date for your quotation`
}

/** The message as it starts out; the admin edits it before anything is sent. */
export function dueDateNoticeMessage({
  kind,
  customerName,
  reference,
  dueAt,
  reason,
}: DueDateNoticeInput) {
  const because = reason.trim() ? ` because ${reason.trim().replace(/[.\s]+$/, '')}` : ''
  const line =
    kind === 'consignment'
      ? `Your shipment${reference ? ` ${reference}` : ''} is now expected by ${kampalaDay(dueAt)}${because}.`
      : `We now expect to send your quotation for ${reference} by ${kampalaDay(dueAt)}${because}.`
  return [greetingLine(customerName), '', line, '', 'Regards,', 'Xtellar'].join('\n')
}
